/**
 * Scan judging API contract — frontend.
 *
 * MIRROR: keep in sync with functions/contracts/scanFeedbackApi.js.
 * Any change to the trait vocabulary, a quota, or the validator MUST update
 * both copies in the same commit. The validator in particular runs on BOTH
 * sides — the client disables SUBMIT with it, the server rejects with it — and
 * a drift between the two shows up as a button that submits and a server that
 * refuses.
 *
 * Deliberate asymmetry: the backend copy additionally exports
 * `feedbackFingerprint()` and `errorResponse()`. Both are server-only (the
 * low-effort streak guard and the soft-error envelope) and shipping them to
 * the client would be dead bundle weight. Everything the client can act on
 * lives in both files.
 *
 * ── WHY A CURATED TEN, AND NOT THE TAXONOMY ─────────────────────────────────
 *
 * The Oracle taxonomy has 7 categories and 37 subcategories. Neither works as
 * a tap target on a stranger's photo:
 *
 *   37 subcategories — nobody distinguishes `texture` from `pores` from
 *   `oiliness` at a glance. The precision is real for a model reading a
 *   close-up; it is noise for a human reading a thumbnail.
 *
 *   7 categories — includes `bone_structure`, which is `carries_products:
 *   false` in the taxonomy and which the product deliberately refuses to
 *   coach. Putting it in an "areas of improvement" list is a personal attack
 *   dressed as feedback, and it is unactionable by construction.
 *
 * So: ten traits a person can actually see, each carrying a `category`
 * back-reference into the taxonomy. That back-reference is the point — the
 * owner-facing aggregate rolls up into the same 7 categories the model scores,
 * so peer opinion and the Oracle become directly comparable.
 *
 * @typedef {Object} JudgeTrait
 * @property {string} key
 * @property {string} label      // full display, museum-text caps
 * @property {string} chip       // short chip label for the judge surface
 * @property {string|null} category  // taxonomy category, null = holistic
 *
 * @typedef {Object} ScanDeckCard
 * @property {string} scan_id
 * @property {string} anonymized_image_url
 * @property {string} card_token
 * @property {number} issued_at
 *
 * @typedef {Object} ScanJudgmentInput
 * @property {string} scan_id
 * @property {string} card_token
 * @property {number} issued_at
 * @property {string[]} strengths
 * @property {string[]} improvements
 *
 * @typedef {Object} ScanJudgmentResponse
 * @property {"success"} status
 * @property {number} responses_total
 * @property {{ done: number, required: number }} progress
 * @property {string|null} unlocked_scan_id
 *
 * @typedef {("UNAUTHENTICATED"|"FORBIDDEN"|"NOT_FOUND"|"INVALID_INPUT"
 *   |"DUPLICATE_FEEDBACK"|"DECK_EMPTY"|"FEEDBACK_DISABLED"|"INVALID_TOKEN"
 *   |"TOO_FAST"|"LOW_EFFORT"|"RATE_LIMITED"|"INTERNAL")} ScanJudgeErrorCode
 */

export const JUDGE_TRAITS = Object.freeze([
  { key: 'skin_clarity', label: 'SKIN CLARITY', chip: 'SKIN', category: 'skin_quality' },
  { key: 'skin_glow', label: 'GLOW', chip: 'GLOW', category: 'skin_quality' },
  { key: 'eye_area', label: 'EYE AREA', chip: 'EYES', category: 'skin_quality' },
  { key: 'hair', label: 'HAIR', chip: 'HAIR', category: 'hair_scalp' },
  { key: 'grooming', label: 'GROOMING', chip: 'GROOMING', category: 'hair_scalp' },
  { key: 'facial_hair', label: 'FACIAL HAIR', chip: 'BEARD', category: 'facial_hair' },
  { key: 'brows', label: 'BROWS', chip: 'BROWS', category: 'brows_lashes' },
  { key: 'smile', label: 'SMILE', chip: 'SMILE', category: 'mouth_smile' },
  { key: 'definition', label: 'DEFINITION', chip: 'DEFINITION', category: 'definition' },
  // Holistic, strengths-only. There is no "your overall presence needs work"
  // affordance — it is unactionable and it is the one selection that would read
  // as an attack on the person rather than a note on a trait.
  { key: 'presence', label: 'PRESENCE', chip: 'PRESENCE', category: null },
]);

/** Strengths-only trait keys — never selectable as an improvement. */
export const STRENGTH_ONLY_TRAITS = Object.freeze(['presence']);

export const TRAIT_KEYS = Object.freeze(JUDGE_TRAITS.map((t) => t.key));

export const IMPROVEMENT_TRAITS = Object.freeze(
  JUDGE_TRAITS.filter((t) => !STRENGTH_ONLY_TRAITS.includes(t.key)),
);

export const IMPROVEMENT_TRAIT_KEYS = Object.freeze(
  IMPROVEMENT_TRAITS.map((t) => t.key),
);

const TRAIT_BY_KEY = Object.freeze(
  JUDGE_TRAITS.reduce((acc, t) => { acc[t.key] = t; return acc; }, {}),
);

/** @param {string} key @returns {JudgeTrait|null} */
export function trait(key) {
  return TRAIT_BY_KEY[key] || null;
}

/** Full museum-text label, falling back to the raw key upcased. */
export function traitLabel(key) {
  return TRAIT_BY_KEY[key]?.label || String(key).toUpperCase();
}

/** Short chip label for the judge surface. */
export function traitChip(key) {
  return TRAIT_BY_KEY[key]?.chip || String(key).toUpperCase();
}

/** Taxonomy category this trait rolls up into, or null for holistic. */
export function traitCategory(key) {
  return TRAIT_BY_KEY[key]?.category ?? null;
}

// ── Quotas and guards ───────────────────────────────────────────────────────

/**
 * Judgments required to unlock one scan.
 *
 * NOTE the adjacent constant: glowupApi exports PEER_SCORE_VOTE_QUOTA = 5, a
 * DIFFERENT quota for a DIFFERENT surface (the two-photo Glowup peer score).
 * They are intentionally separate constants with separate names. Never import
 * one where the other is meant.
 */
export const UNLOCK_FEEDBACK_QUOTA = 10;

export const MIN_PICKS = 2;
export const MAX_PICKS = 4;

// ── THE HIDDEN OVERALL RATING ───────────────────────────────────────────────
//
// Every judgment carries an integer 1-10 that the person being judged NEVER
// sees. It is training data and the basis of a future percentile, not feedback.
//
// NAMED `rating`, NOT `overall_score`, on purpose. The verdict's
// `overall_score` is on a 1-100 scale (see fuseScores). Two fields with the
// same name on different scales is exactly how a percentile comes out 10x
// wrong six months from now, in a way no test would catch.
//
// WHY REQUIRED RATHER THAN OPTIONAL. An optional rating produces a
// self-selected sample: the judges who bother to rate are systematically
// different from the ones who do not, and that bias is unfixable after the
// fact. Less data beats skewed data. The cost is one more interaction on the
// surface that gates every unlock, which is a real price and was paid
// deliberately.
//
// WHERE IT MUST NOT GO — all three are asserted in the tests:
//   * not in any response body       (it is hidden from the scan's owner)
//   * not in `peer_feedback`         (that aggregate IS returned to the owner)
//   * not in `feedbackFingerprint`   (see the note on that function)
export const RATING_MIN = 1;
export const RATING_MAX = 10;

/** @param {unknown} v @returns {boolean} */
export function isJudgeScore(v) {
  return Number.isInteger(v) && v >= RATING_MIN && v <= RATING_MAX;
}

/**
 * Server-enforced minimum time on a card. The deck mints `issued_at` inside a
 * signed token; a submission arriving faster than this is rejected. Any
 * dwell_ms the client reports is telemetry only and never gates anything —
 * a client-reported timer is trivially forged.
 */
export const MIN_DWELL_MS = 4000;

/** How long a minted deck card stays submittable. */
export const CARD_TOKEN_TTL_MS = 15 * 60 * 1000;

/** Identical (strengths, improvements) tuples in a row before LOW_EFFORT. */
export const MAX_FINGERPRINT_STREAK = 4;

/** Per-judge hourly ceiling. */
export const MAX_FEEDBACK_PER_HOUR = 60;

export const SCAN_JUDGE_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  INVALID_INPUT: 'INVALID_INPUT',
  DUPLICATE_FEEDBACK: 'DUPLICATE_FEEDBACK',
  DECK_EMPTY: 'DECK_EMPTY',
  FEEDBACK_DISABLED: 'FEEDBACK_DISABLED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOO_FAST: 'TOO_FAST',
  LOW_EFFORT: 'LOW_EFFORT',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
});

/** Reason codes on the judgment_rejected event — these tune the guards. */
export const REJECTION_REASONS = Object.freeze({
  INVALID_INPUT: 'invalid_input',
  SELF_JUDGE: 'self_judge',
  FEEDBACK_DISABLED: 'feedback_disabled',
  DUPLICATE: 'duplicate',
  BAD_TOKEN: 'bad_token',
  TOO_FAST: 'too_fast',
  EXPIRED_TOKEN: 'expired_token',
  LOW_EFFORT: 'low_effort',
  RATE_LIMITED: 'rate_limited',
});

// ── Validation ──────────────────────────────────────────────────────────────

function validPickList(list, allowedKeys) {
  if (!Array.isArray(list)) return false;
  if (list.length < MIN_PICKS || list.length > MAX_PICKS) return false;
  if (new Set(list).size !== list.length) return false; // no repeats
  return list.every((k) => typeof k === 'string' && allowedKeys.includes(k));
}

/**
 * Pure validator, run on both sides. Returns `{ ok, message }` rather than
 * throwing so the client can use it to drive a disabled SUBMIT button.
 *
 * @param {unknown} data
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateScanFeedbackInput(data) {
  if (!data || typeof data !== 'object') {
    return { ok: false, message: 'Missing judgment payload.' };
  }
  const {
    scan_id: scanId, strengths, improvements, rating: judgeScore,
  } = data;

  if (typeof scanId !== 'string' || !scanId) {
    return { ok: false, message: 'scan_id is required.' };
  }
  if (!isJudgeScore(judgeScore)) {
    // Two different messages, because these are two different problems and
    // only one of them is the user's. A client that never collected a rating
    // is a STALE BUNDLE mid-deploy — its UI has no control to point at, so
    // "give a rating" would be advice the user cannot act on. Tell them to
    // reload instead. Anything else is an ordinary validation failure.
    return judgeScore === undefined || judgeScore === null
      ? { ok: false, message: 'Reload the page to continue reading.' }
      : {
        ok: false,
        message: `Give an overall rating from ${RATING_MIN} to ${RATING_MAX}.`,
      };
  }
  if (!validPickList(strengths, TRAIT_KEYS)) {
    return {
      ok: false,
      message: `Select ${MIN_PICKS}-${MAX_PICKS} distinct strengths.`,
    };
  }
  if (!validPickList(improvements, IMPROVEMENT_TRAIT_KEYS)) {
    return {
      ok: false,
      message: `Select ${MIN_PICKS}-${MAX_PICKS} distinct areas to work on.`,
    };
  }
  // A trait cannot be both a strength and an area to work on. Without this the
  // aggregate reads "SKIN — 7 strong / 7 weak" and means nothing.
  const overlap = improvements.filter((k) => strengths.includes(k));
  if (overlap.length > 0) {
    return {
      ok: false,
      message: 'A trait cannot be both a strength and an area to work on.',
    };
  }
  return { ok: true };
}

/** True when the current selection is submittable. Drives the SUBMIT button. */
export function canSubmitSelection(strengths, improvements, judgeScore) {
  return validateScanFeedbackInput({
    scan_id: 'x', strengths, improvements, rating: judgeScore,
  }).ok;
}

// ── Aggregate helpers ───────────────────────────────────────────────────────

/** The zero-value written on a scan's first judgment. */
export function emptyPeerFeedback() {
  return {
    strengths: {},
    improvements: {},
    responses_count: 0,
    unseen_count: 0,
    last_feedback_at: null,
  };
}

/**
 * Sort a sparse `{trait_key: count}` map into a descending display list.
 * Ties break on the vocabulary order so the readout is stable between renders
 * instead of reshuffling on every fetch.
 *
 * @param {Object<string, number>} counts
 * @param {number} [limit]
 * @returns {{ key: string, label: string, count: number }[]}
 */
export function rankTraitCounts(counts, limit) {
  if (!counts || typeof counts !== 'object') return [];
  const rows = Object.entries(counts)
    .filter(([key, count]) => TRAIT_BY_KEY[key] && count > 0)
    .map(([key, count]) => ({ key, label: traitLabel(key), count }))
    .sort((a, b) => (
      b.count - a.count || TRAIT_KEYS.indexOf(a.key) - TRAIT_KEYS.indexOf(b.key)
    ));
  return typeof limit === 'number' ? rows.slice(0, limit) : rows;
}

/**
 * Roll a sparse trait-count map up into taxonomy categories, so peer opinion
 * can sit beside the model's own category scores. Holistic traits (category
 * null) are dropped — they have no taxonomy counterpart.
 *
 * @param {Object<string, number>} counts
 * @returns {Object<string, number>}
 */
export function rollUpToCategories(counts) {
  const out = {};
  if (!counts || typeof counts !== 'object') return out;
  for (const [key, count] of Object.entries(counts)) {
    const category = traitCategory(key);
    if (!category || !(count > 0)) continue;
    out[category] = (out[category] || 0) + count;
  }
  return out;
}

/** @param {unknown} obj @returns {obj is ScanJudgmentResponse} */
export function isScanJudgmentResponse(obj) {
  return Boolean(
    obj && typeof obj === 'object'
      && obj.status === 'success'
      && typeof obj.responses_total === 'number'
      && obj.progress && typeof obj.progress.required === 'number',
  );
}

/**
 * Sample fixture for tests and component development.
 * @type {ScanDeckCard}
 */
export const SAMPLE_SCAN_DECK_CARD = Object.freeze({
  scan_id: '00000000-0000-0000-0000-000000000000',
  anonymized_image_url: '/sample/anonymized.jpg',
  card_token: 'sample-token',
  issued_at: 0,
});
